import { ColumnPickerDialog } from "@serenity-is/corelib";
import { isDataColumn } from "./GridExport";

/**
 * Keeps Serenity's internal columns out of the Column Picker.
 *
 * THE PROBLEM
 * `ColumnPickerDialog` lists whatever `getAllColumns()` returns, with no notion of a column
 * that should not be user-toggleable. On the Franchisee grid that means the seven real
 * columns were listed alongside three pieces of plumbing — `[x]` (the selection checkbox),
 * `__expand` (the row-expand chevron) and `__rowActions` — which are not data, carry no
 * meaning to a user, and BREAK THE GRID when unticked: hiding `__expand` removes the way to
 * open a franchisee's programmes, hiding `[x]` removes bulk selection. The prototype's own
 * Columns popover lists only the real columns.
 *
 * THE FIX, AND WHY IT IS NOT CSS
 * Hiding those rows with CSS would leave them in the dialog's model, so "Restore Defaults"
 * and the toggle-all checkbox would still act on them. Instead the dialog is opened with its
 * own documented extension points: `columns` (what to list) and `reorderColumns` (how to
 * apply a new order). Nothing private is touched.
 *
 * ⚠ TWO SLEEKGRID BEHAVIOURS MAKE THIS WORK, AND ONE OF THEM NEEDS A MERGE:
 *  - `reorderColumns(ids)` runs `sortToDesiredOrderAndKeepRest`, which drags any UNLISTED
 *    column along with its neighbours. So passing only the real ids keeps the select and
 *    expand columns leading and the row actions trailing, with no work from us.
 *  - `setVisible`, however, is absolute: `internalSetVisibleColumns` sets
 *    `visible = idSet.has(col.id)` for EVERY column, so a filtered list would mark the three
 *    internal columns invisible — the checkbox, chevron and row actions would vanish the
 *    first time a user reordered a column or hit Restore Defaults. The internal ids are
 *    therefore appended to `setVisible` before it is applied. That is the whole subtlety
 *    here; the rest is a pass-through.
 *
 * ⚠ Per-column ticks do NOT go through `reorderColumns` — the dialog mutates `column.visible`
 * and calls `invalidateColumns()` directly, so unlisted columns are untouched on that path.
 *
 * Patching the static `createToolButton` rather than editing each grid is the same choice
 * ListPageChrome makes by hooking `DataGrid.onAfterInit`: every list page in the app is
 * covered, including ones added later, and no *Grid.ts has to know this exists.
 */
export function installColumnPickerFilter() {
    const original = (ColumnPickerDialog as any).createToolButton;
    if (typeof original !== "function" || (original as any).__bwDesignSystem) return;

    const patched = function (this: any, optOrDataGrid: any) {
        // Let Serenity build the button as usual — hint, icon, css class, and the route
        // handler it wires up for "#columns" — then replace only what opens.
        const button = original.call(this, optOrDataGrid);

        const dataGrid = optOrDataGrid && "getGrid" in optOrDataGrid
            ? optOrDataGrid
            : optOrDataGrid?.dataGrid;
        if (!dataGrid) return button;

        button.onClick = () => {
            const slick = dataGrid.getGrid?.();
            const all: any[] = slick?.getAllColumns?.() || [];
            const internalIds = all.filter(c => !isDataColumn(c)).map(c => c.id);

            (ColumnPickerDialog as any).openDialog({
                dataGrid,
                columns: () => all.filter(isDataColumn),
                reorderColumns: (columnIds: string[], setVisible?: string[]) => {
                    slick?.reorderColumns?.(columnIds, {
                        notify: true,
                        // See the note above: absolute list, so the plumbing has to be in it.
                        setVisible: setVisible ? [...setVisible, ...internalIds] : undefined,
                    });
                    return true;
                },
            });
        };

        return button;
    };

    (patched as any).__bwDesignSystem = true;
    (ColumnPickerDialog as any).createToolButton = patched;
}
